import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import useSiteSettings from '@/hooks/useSiteSettings';

const STAFF = ['super_admin', 'admin', 'deal_manager', 'content_manager', 'investor_relations', 'view_only'];
const FONT_HEADING = "'Cormorant Garamond', Cambria, Georgia, serif";
const FONT_BODY = "'Jost', sans-serif";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  const s = useSiteSettings() || {};
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/portal';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const brand = s.brand_name || 'SÀWAI CAPITAL';
  const ctaLoggedIn = s.nav_cta_logged_in || 'Enter Dealroom';
  const ctaLoggedOut = s.nav_cta_logged_out || 'Investor Login';
  const isStaff = user && STAFF.includes(user.role);
  const dealroomPath = isStaff ? '/admin' : '/portal';

  const navLinks = [
    { href: '#asset-classes', label: s.nav_label_asset_classes || 'Asset Classes', testid: 'nav-asset-classes', type: 'hash' },
    { href: '#how-it-works', label: s.nav_label_process || 'Process', testid: 'nav-how-it-works', type: 'hash' },
    { href: '#who-we-serve', label: s.nav_label_investors || 'Investors', testid: 'nav-who-we-serve', type: 'hash' },
    { href: '#brand-story', label: s.nav_label_about || 'About', testid: 'nav-about', type: 'hash' },
    { to: '/blog', label: s.nav_label_blog || 'Blog', testid: 'nav-blog', type: 'route' },
    { to: '/newsroom', label: s.nav_label_newsroom || 'Newsroom', testid: 'nav-newsroom', type: 'route' },
    { to: '/leadership', label: s.nav_label_team || 'Team', testid: 'nav-leadership', type: 'route' },
  ];

  // Hash links (#asset-classes, etc.) only exist on the landing page.
  // If user is anywhere else, navigate to "/" then scroll to the section after render.
  const handleHashClick = (e, hash) => {
    e.preventDefault();
    setMobileOpen(false);
    const targetId = hash.replace('#', '');
    if (location.pathname === '/') {
      document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/', { state: { scrollTo: targetId } });
    }
  };

  // After landing page mounts via cross-page navigation, perform the scroll.
  useEffect(() => {
    if (location.pathname === '/' && location.state?.scrollTo) {
      const id = location.state.scrollTo;
      // Wait one tick so the landing sections are mounted.
      const t = setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
        // Clear state so refresh doesn't re-scroll
        window.history.replaceState({}, '');
      }, 80);
      return () => clearTimeout(t);
    }
  }, [location.pathname, location.state]);

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 w-full z-50 transition-all duration-500 ${
          scrolled ? 'bg-[#1A1A1A]/95 backdrop-blur-md border-b border-[#A0743A]/20' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <span className="text-[#A0743A] text-xl tracking-[0.15em] font-semibold" style={{ fontFamily: FONT_HEADING }}>
              {brand}
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map(link => (
              link.type === 'hash' ? (
                <a key={link.testid} href={link.href} onClick={(e) => handleHashClick(e, link.href)} className="text-[#FAF8F5] hover:text-[#A0743A] transition-colors text-xs uppercase tracking-[0.15em]" style={{ fontFamily: FONT_BODY }} data-testid={link.testid}>{link.label}</a>
              ) : (
                <Link key={link.testid} to={link.to} className="text-[#FAF8F5] hover:text-[#A0743A] transition-colors text-xs uppercase tracking-[0.15em]" style={{ fontFamily: FONT_BODY }} data-testid={link.testid}>{link.label}</Link>
              )
            ))}
            {user ? (
              <Link to={dealroomPath} className="bg-[#A0743A] text-[#FAF8F5] px-6 py-2.5 text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#C9A96E] transition-colors" data-testid="nav-portal-btn">{ctaLoggedIn}</Link>
            ) : (
              <button onClick={handleLogin} className="bg-[#A0743A] text-[#FAF8F5] px-6 py-2.5 text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#C9A96E] transition-colors" data-testid="nav-login-btn">{ctaLoggedOut}</button>
            )}
          </div>

          {/* Mobile Hamburger */}
          <button className="md:hidden text-[#FAF8F5] z-[60]" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
            {mobileOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </nav>

      {/* Mobile Full-Screen Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[55] bg-[#1A1A1A] flex flex-col items-center justify-center" data-testid="mobile-menu-overlay">
          <button className="absolute top-6 right-6 text-[#FAF8F5]" onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close">
            <X size={28} />
          </button>
          <div className="flex flex-col items-center gap-10">
            {navLinks.map(link => (
              link.type === 'hash' ? (
                <a key={link.testid} href={link.href} onClick={(e) => handleHashClick(e, link.href)} className="text-[#A0743A] text-2xl tracking-[0.15em] uppercase" style={{ fontFamily: FONT_HEADING }}>{link.label}</a>
              ) : (
                <Link key={link.testid} to={link.to} onClick={() => setMobileOpen(false)} className="text-[#A0743A] text-2xl tracking-[0.15em] uppercase" style={{ fontFamily: FONT_HEADING }}>{link.label}</Link>
              )
            ))}
            <div className="w-12 h-[1px] bg-[#C9A96E]/40 my-2" />
            {user ? (
              <Link to={dealroomPath} onClick={() => setMobileOpen(false)} className="bg-[#A0743A] text-[#FAF8F5] px-10 py-4 text-sm uppercase tracking-[0.15em] font-semibold">{ctaLoggedIn}</Link>
            ) : (
              <button onClick={() => { setMobileOpen(false); handleLogin(); }} className="bg-[#A0743A] text-[#FAF8F5] px-10 py-4 text-sm uppercase tracking-[0.15em] font-semibold">{ctaLoggedOut}</button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
